import React from 'react';
import styled from 'styled-components';
import {
  Flexbox,
  ThemeButton,
  TextElement,
  Card
} from '@ibinder/ui-common-core';
import { OrgPropertyDisplay } from './components/OrgPropertyDisplay';
import { getFormatedDate, iBinderHeaderEnum } from '@ibinder/common-utils';

import { GetFlag } from './components/GetFlag';
import { useFeatureToggle, useIBTranslation } from '@ibinder/ui-common-hooks';
import { DEFAULT_PLATFORM, platformData } from '../CreateOrgForm';
import { PackageHeader } from './OrganizationPackages';
import {
  BinderUsageDisplay,
  useOrganizationBindersUsage
} from './components/BindersDisplay';
import upperFirst from 'lodash/upperFirst';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_ORG_SETTINGS,
  GET_ORGANIZATION_SUMMARY,
  UPDATE_ORG_THRESHOLDS
} from 'Shared/api/queries/getOrganization.query';
import { ThemeTextField } from '@ibinder/ui-common-core';
import { buttonTypes } from '@ibinder/ui-common-styles';

const StyledFlexbox = styled(Flexbox)`
  gap: 1rem;
`;
export const FlagFlexbox = styled(Flexbox)`
  > svg {
    height: 1rem;
  }
  > span {
    margin-left: 0.5rem;
  }
`;

const Row = styled(Flexbox)`
  gap: 1rem;
  width: 100%;
`;

const Column = styled(Flexbox)`
  width: 50%;
`;

const SUMMARY_LAYOUT = [
  // Storage metrics
  ['totalSize', 'totalDeletedSize'],
  // Document counts
  ['documentsCount', 'deletedDocumentsCount'],
  // Version counts
  ['documentVersionsCount', 'deletedVersionsCount']
];

type OrganizationInfoProps = {
  organizationData: any;
  platform: any;
  onDirtyChange?: (dirty: boolean) => void;
};

const OrganizationInfo = ({
  organizationData,
  platform,
  onDirtyChange
}: OrganizationInfoProps) => {
  const { isEnabled: useSelectOrgPackage } = useFeatureToggle(
    // @ts-ignore -- TODO add to feat const /Jonathan 2026-01-12
    'Configcat_selectPackageOnOrgCreation'
  );
  const { data: organizationSummaryData, loading: loadingOrganizationSummary } =
    useQuery(GET_ORGANIZATION_SUMMARY, {
      context: {
        headers: { [iBinderHeaderEnum.TENANTID]: organizationData?.id }
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        organizationId: organizationData?.id
      },
      skip: !organizationData?.id
    });
  const { data = { settings: [] }, loading: loadingOrgSettings } = useQuery(
    GET_ORG_SETTINGS,
    {
      fetchPolicy: 'no-cache',
      context: {
        headers: { [iBinderHeaderEnum.TENANTID]: organizationData?.id }
      },
      skip: !organizationData?.id
    }
  );

  const [updateOrganizationThresholds] = useMutation(UPDATE_ORG_THRESHOLDS);
  const isDirty = React.useRef(false);

  const { t } = useIBTranslation();

  React.useEffect(() => {
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  const [thresholds, dispatch] = React.useReducer(
    (
      state: Record<'facility' | 'project' | 'tender', number>,
      data: Partial<{ [key in keyof typeof state]: number }>
    ) => ({
      ...state,
      ...data
    }),
    {
      facility: 0,
      project: 0,
      tender: 0
    }
  );

  const handleSave = () => {
    updateOrganizationThresholds({
      variables: {
        id: organizationData.id,
        body: {
          FacilityThreshold: thresholds.facility,
          ProjectThreshold: thresholds.project,
          TenderThreshold: thresholds.tender
        }
      }
    });

    isDirty.current = false;
    onDirtyChange?.(false);
  };

  React.useEffect(() => {
    const thresholdSettings = data.settings.reduce((acc, setting) => {
      if (
        setting.key === 'facility_threshold' ||
        setting.key === 'project_threshold' ||
        setting.key === 'tender_threshold'
      ) {
        acc[setting.key] = setting.value;
      }
      return acc;
    }, {});

    // Calculate next values from API and only apply if they differ and form is not dirty
    const next = {
      facility: Number(thresholdSettings.facility_threshold) || 0,
      project: Number(thresholdSettings.project_threshold) || 0,
      tender: Number(thresholdSettings.tender_threshold) || 0
    };

    const changed =
      next.facility !== thresholds.facility ||
      next.project !== thresholds.project ||
      next.tender !== thresholds.tender;

    if (!isDirty.current && changed) {
      dispatch(next);
    }
  }, [
    data.settings,
    thresholds.facility,
    thresholds.project,
    thresholds.tender
  ]);

  const { total, projects, facilities, selections, tenders, loading } =
    useOrganizationBindersUsage(organizationData?.id, t);

  const convertBytesToMB = (bytes) => {
    if (bytes === null || bytes === undefined || bytes === 0) {
      return `0 MB`;
    }
    if (bytes < 1024 * 1024) {
      return `< 1 MB`;
    } else {
      return `${Math.round(bytes / 1024 / 1024).toLocaleString()} MB`;
    }
  };

  const getSummaryValue = (key, convertToMB = false) => {
    const value = organizationSummaryData?.organizationSummary?.[key];
    if (convertToMB) {
      return convertBytesToMB(value);
    }
    return value?.toLocaleString() || '0';
  };

  const summaryData = {
    totalSize: getSummaryValue('totalSizeInBytes', true),
    totalDeletedSize: getSummaryValue('totalDeletedSizeInBytes', true),
    deletedDocumentsCount: getSummaryValue('deletedDocumentsCount'),
    deletedVersionsCount: getSummaryValue('deletedVersionsCount'),
    documentVersionsCount: getSummaryValue('documentVersionsCount'),
    documentsCount: getSummaryValue('documentsCount')
  };

  const infoString = (resource) => (state) =>
    `${resource[state]} ${t(`state.${state}`)}`;

  const renderThresholdInput = (key: keyof typeof thresholds) => {
    if (!useSelectOrgPackage) {
      return (
        <OrgPropertyDisplay label={t('maxActive')} value={thresholds[key]} />
      );
    }
    return (
      <ThemeTextField
        inputMode="numeric"
        label={t('maxActive')}
        loading={loadingOrgSettings}
        min={0}
        onBlur={(e) => {
          const value = e.currentTarget.valueAsNumber;
          // On blur, ensure we have a valid non-negative integer
          dispatch({
            [key]: Math.max(0, isNaN(value) ? 0 : Math.floor(value))
          });
        }}
        onChange={(e) => {
          isDirty.current = true;
          onDirtyChange?.(true);
          const value = e.currentTarget.valueAsNumber;
          dispatch({
            // Set as empty string if NaN, will be set to 0 on blur
            [key]: isNaN(value) ? '' : Math.floor(value)
          });
        }}
        step="1"
        type="number"
        value={thresholds[key].toString()} // toString() removes leading 0
      />
    );
  };

  return (
    <StyledFlexbox flexDirection="column">
      <Flexbox>
        <OrgPropertyDisplay
          loading={!organizationData}
          label={t('organizationId')}
          showCopy={true}
          value={organizationData?.id}
        />
      </Flexbox>
      <Row>
        <Column>
          <OrgPropertyDisplay
            loading={!organizationData}
            label={t('marketId')}
            showCopy={true}
            value={organizationData?.market?.id}
          />
        </Column>
        <Column>
          <OrgPropertyDisplay
            loading={!organizationData}
            label={t('market')}
            value={
              <FlagFlexbox alignItems="center">
                <GetFlag countryName={organizationData?.market?.name} />
                <span>{organizationData?.market?.name}</span>
              </FlagFlexbox>
            }
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <OrgPropertyDisplay
            loading={!organizationData}
            label={t('createdDate')}
            value={getFormatedDate(organizationData?.created)}
          />
        </Column>
        <Column>
          <OrgPropertyDisplay
            loading={!organizationData}
            label={t('platform')}
            value={
              platformData.find((p) =>
                platform && platform !== null
                  ? p.Key === platform
                  : p.Key === DEFAULT_PLATFORM
              )?.Value
            }
          />
        </Column>
      </Row>
      <PackageHeader>{t('organizationSummary')}*</PackageHeader>
      {loadingOrganizationSummary ? (
        <Card loading={true} />
      ) : (
        SUMMARY_LAYOUT.map((rowData, rowIndex) => (
          <Row key={`summary-row-${rowIndex}`}>
            {rowData.map((key, colIndex) => (
              <Column key={`summary-col-${rowIndex}-${colIndex}`}>
                <OrgPropertyDisplay label={t(key)} value={summaryData[key]} />
              </Column>
            ))}
          </Row>
        ))
      )}
      <Row>
        <TextElement fontStyle="extraExtraSmallFont">
          *{t('filesExcludedDisclaimer')}
        </TextElement>
      </Row>
      <PackageHeader>
        {upperFirst(t('binders'))} {total}
      </PackageHeader>
      {loading ? (
        <Card loading={true} />
      ) : (
        <>
          <Row>
            <Column>
              <BinderUsageDisplay
                title={`${t('projectBinders')} ${projects.totalCount}`}
                info={['activeCount', 'archivedCount', 'warrantyCount']
                  .map(infoString(projects))
                  .join(', ')}
              />
            </Column>
            <Column>{renderThresholdInput('project')}</Column>
          </Row>
          <Row>
            <Column>
              <BinderUsageDisplay
                title={`${t('facilityBinders')} ${facilities.totalCount}`}
                info={['activeCount', 'archivedCount', 'warrantyCount']
                  .map(infoString(facilities))
                  .join(', ')}
              />
            </Column>
            <Column>{renderThresholdInput('facility')}</Column>
          </Row>
          <Row>
            <Column>
              <BinderUsageDisplay
                title={`${t('etenderingBinders')} ${tenders.totalCount}`}
                info={['activeCount', 'archivedCount', 'endedCount']
                  .map(infoString(tenders))
                  .join(', ')}
              />
            </Column>
            <Column>{renderThresholdInput('tender')}</Column>
          </Row>
          <Row>
            <Column>
              <BinderUsageDisplay
                title={`${t('selectionBinders')} ${selections.totalCount}`}
              />
            </Column>
            <Column>
              {isDirty.current && (
                <Flexbox justifyContent="flex-end">
                  <ThemeButton
                    buttonType={buttonTypes.Primary}
                    onClick={handleSave}
                  >
                    {t('save')}
                  </ThemeButton>
                </Flexbox>
              )}
            </Column>
          </Row>
        </>
      )}
    </StyledFlexbox>
  );
};
export default OrganizationInfo;
